import { useId } from "react";
import { cn } from "@/lib/utils";
import { formatNumber, formatPercent } from "./format.ts";
import { type ChartTone, fillClass } from "./frame.tsx";

export interface MeterProps {
  /** What is measured, e.g. "OCR pages this month". */
  label: string;
  value: number;
  /** The limit; absent or 0 means no limit (the meter shows the value only). */
  max?: number | null;
  formatValue?: (value: number) => string;
  /** E.g. "of 5,000": defaults to "value / max" in the locale. */
  valueText?: string;
  tone?: ChartTone;
  className?: string;
}

/**
 * Use against a limit. The figures are written out ("1,200 / 5,000 · 24%"), so the bar is
 * never the only carrier; past the limit the bar turns to the danger mark.
 */
export function Meter({
  label,
  value,
  max,
  formatValue = formatNumber,
  valueText,
  tone = 1,
  className,
}: MeterProps) {
  const labelId = useId();
  const limited = typeof max === "number" && max > 0;
  const share = limited ? value / max : 0;
  const text =
    valueText ??
    (limited
      ? `${formatValue(value)} / ${formatValue(max)} · ${formatPercent(share)}`
      : formatValue(value));
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 text-sm">
        <span id={labelId} className="text-chart-label">
          {label}
        </span>
        <span className="tabular-nums text-fg">{text}</span>
      </div>
      {limited ? (
        <div>
          <meter className="sr-only" aria-labelledby={labelId} min={0} max={max} value={value} />
          <svg
            viewBox="0 0 100 4"
            preserveAspectRatio="none"
            className="h-2 w-full"
            aria-hidden="true"
          >
            <rect x={0} y={0} width={100} height={4} rx={1} className="fill-chart-track" />
            <rect
              x={0}
              y={0}
              width={Math.min(share, 1) * 100}
              height={4}
              rx={1}
              className={fillClass(share > 1 ? 4 : tone)}
            />
          </svg>
        </div>
      ) : null}
    </div>
  );
}
